import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import type { Market } from '@//config/domain-locale-map';
import Locations from '@//utils/vehicles-search/known-locations';
import { resolveLocationPathKeyFromParams } from '@//utils/vehicles-search/utils';

interface DynamicSeoParams {
  searchParams: URLSearchParams;
  market: Market;
  brandName: string;
}

interface DynamicSeoResult {
  title?: string;
  ingress?: string;
  metaTitle?: string;
  metaDescription?: string;
}

const useDynamicSeo = ({
  searchParams,
  market,
  brandName,
}: DynamicSeoParams): DynamicSeoResult | null => {
  const t = useTranslations('pages.search.seo');
  const tVehicleGlossary = useTranslations('utils.vehicleGlossary');

  const dynamicSeo = useMemo(() => {
    // Extract single-value filters - only proceed if exactly one value per type
    const filters = extractSingleValueFilters(searchParams);
    if (!filters) {
      return null; // Multiple values detected, use fallbacks
    }

    // Generate vehicle key based on manufacturer and modelSeries
    const vehicle = generateVehicleKey(filters, t);

    // Extract location information
    const location = extractLocationFromParams(searchParams);

    // Generate SEO values based on filter combinations
    return generateSeoValues(filters, vehicle, location, brandName, t, tVehicleGlossary);
  }, [searchParams.toString(), market, brandName, t, tVehicleGlossary]);

  // Update document title when dynamic SEO changes (client-side only)
  useEffect(() => {
    if (typeof window !== 'undefined' && dynamicSeo?.metaTitle) {
      document.title = dynamicSeo.metaTitle;
    }
  }, [dynamicSeo?.metaTitle]);

  return dynamicSeo;
};

// Helper function to extract filters, returning null if multiple values found for any type
function extractSingleValueFilters(searchParams: URLSearchParams) {
  const supportedFilters = [
    'manufacturer',
    'modelSeries',
    'modelYear',
    'gearboxType',
    'fuelTypes',
    'color',
    'chassis',
    'price',
    'mileage',
    'odometerValueAsKm',
  ];

  const filters: Record<string, string> = {};

  // Check for segment in properties.segment
  const segment = searchParams.get('properties.segment');
  if (segment) {
    const allSegments = searchParams.getAll('properties.segment');
    if (allSegments.length > 1) return null;
    filters['properties.segment'] = segment;
  }

  // Check each supported filter
  for (const filterKey of supportedFilters) {
    // Handle range filters
    if (['modelYear', 'price', 'mileage', 'odometerValueAsKm'].includes(filterKey)) {
      const minValue = searchParams.get(`${filterKey}.min`);
      const maxValue = searchParams.get(`${filterKey}.max`);

      if (minValue && maxValue) {
        if (minValue === maxValue) {
          // Same value: "2017–2017" becomes "2017"
          filters[filterKey] = minValue;
        } else {
          // Range: "2017–2025"
          filters[filterKey] = `${minValue}–${maxValue}`;
        }
      } else if (minValue) {
        filters[filterKey] = minValue;
      } else if (maxValue) {
        filters[filterKey] = maxValue;
      }
    } else {
      const values = searchParams.getAll(filterKey);
      if (values.length > 1) {
        return null; // Multiple values found, don't generate dynamic SEO
      }
      if (values.length === 1) {
        filters[filterKey] = values[0];
      }
    }
  }

  // Check if we have location parameters even if no vehicle filters
  const hasLocationParams =
    searchParams.get('lat') &&
    searchParams.get('lon') &&
    searchParams.get('position.name') &&
    searchParams.get('position.location');

  // Only return filters if we have at least one filter OR location
  return Object.keys(filters).length > 0 || hasLocationParams ? filters : null;
}

function generateVehicleKey(filters: Record<string, string>, t: any): string {
  const { manufacturer, modelSeries } = filters;

  if (manufacturer && modelSeries) {
    return `${manufacturer} ${modelSeries}`;
  }

  if (manufacturer) {
    return manufacturer;
  }

  if (modelSeries) {
    return modelSeries;
  }

  // Fallback to generic vehicle term
  return t('fallbackValues.vehicle');
}

function extractLocationFromParams(searchParams: URLSearchParams): string | null {
  const locationKey = resolveLocationPathKeyFromParams(searchParams);
  if (locationKey && Locations[locationKey]) {
    return stripCountryFromLocation(Locations[locationKey].name);
  }

  // Fallback to position.name if available
  const positionName = searchParams.get('position.name');
  if (positionName) {
    try {
      const decoded = decodeURIComponent(positionName);
      // Sanitize: only allow letters, numbers, spaces, comma, and common location characters
      const sanitized = decoded.replace(/[^a-zA-ZåäöÅÄÖæøÆØ\s,.-]/g, '').trim();
      return sanitized ? stripCountryFromLocation(sanitized) : null;
    } catch {
      // Invalid URL encoding, ignore
      return null;
    }
  }
  return null;
}

// Helper function to strip country suffix from location names
function stripCountryFromLocation(location: string): string {
  // Remove common country suffixes like ", Sweden", ", Norge", ", Norway"
  return location.replace(/,\s*(Sweden|Norge|Norway|Sverige)$/i, '').trim();
}

function generateSeoValues(
  filters: Record<string, string>,
  vehicle: string,
  location: string | null,
  brandName: string,
  t: any,
  tVehicleGlossary: any,
): DynamicSeoResult {
  const hasLocation = !!location;
  const hasOtherFilters = hasNonVehicleFilters(filters);

  // Determine which template category to use
  if (hasLocation && hasOtherFilters) {
    // Other filters + location
    const { filterKey, filterValue } = getFirstNonVehicleFilter(filters, tVehicleGlossary);
    return {
      title: t('otherFiltersInLocation.heading', { vehicle, filterKey, filterValue, location }),
      ingress: t('otherFiltersInLocation.description', {
        brandName,
        vehicle,
        filterKey,
        filterValue,
        location,
      }),
      metaTitle: t('otherFiltersInLocation.metaTitle', {
        vehicle,
        filterKey,
        filterValue,
        location,
      }),
      metaDescription: t('otherFiltersInLocation.metaDescription', {
        brandName,
        vehicle,
        filterKey,
        filterValue,
        location,
      }),
    };
  }

  if (hasOtherFilters) {
    // Other filters only
    const { filterKey, filterValue } = getFirstNonVehicleFilter(filters, tVehicleGlossary);
    return {
      title: t('otherFilters.heading', { vehicle, filterKey: filterKey, filterValue }),
      ingress: t('otherFilters.description', { brandName, vehicle, filterKey, filterValue }),
      metaTitle: t('otherFilters.metaTitle', { vehicle, filterKey, filterValue }),
      metaDescription: t('otherFilters.metaDescription', {
        brandName,
        vehicle,
        filterKey,
        filterValue,
      }),
    };
  }

  if (hasLocation) {
    // Location only
    return {
      title: t('location.heading', { vehicle, location }),
      ingress: t('location.description', { brandName, vehicle, location }),
      metaTitle: t('location.metaTitle', { vehicle, location }),
      metaDescription: t('location.metaDescription', { brandName, vehicle, location }),
    };
  }

  // Vehicle only (manufacturer/modelSeries)
  return {
    title: t('vehicle.heading', { vehicle }),
    ingress: t('vehicle.description', { brandName, vehicle }),
    metaTitle: t('vehicle.metaTitle', { vehicle }),
    metaDescription: t('vehicle.metaDescription', { brandName, vehicle }),
  };
}

function hasNonVehicleFilters(filters: Record<string, string>): boolean {
  const nonVehicleKeys = [
    'modelYear',
    'gearboxType',
    'fuelTypes',
    'color',
    'chassis',
    'properties.segment',
    'price',
    'mileage',
    'odometerValueAsKm',
  ];
  return nonVehicleKeys.some((key) => filters[key]);
}

function getFirstNonVehicleFilter(
  filters: Record<string, string>,
  tVehicleGlossary: any,
): {
  filterKey: string;
  filterValue: string;
} {
  const nonVehicleKeys = [
    'modelYear',
    'gearboxType',
    'fuelTypes',
    'color',
    'chassis',
    'properties.segment',
    'price',
    'mileage',
    'odometerValueAsKm',
  ];

  for (const key of nonVehicleKeys) {
    if (filters[key]) {
      // Get the translated filter key name
      let translatedFilterKey = key;
      try {
        // Map keys to their glossary equivalents
        let translationKey = key;
        if (key === 'fuelTypes') {
          translationKey = 'fuelType';
        } else if (key === 'mileage' || key === 'odometerValueAsKm') {
          translationKey = 'odometerReading.label';
        } else if (key === 'price') {
          translationKey = 'price.label';
        } else if (key === 'properties.segment') {
          translationKey = 'segment';
        }
        translatedFilterKey = tVehicleGlossary(translationKey);
      } catch {
        // Fallback to original key if translation fails
        translatedFilterKey = key;
      }

      return {
        filterKey: translatedFilterKey.toLowerCase(),
        filterValue: filters[key].toLowerCase(),
      };
    }
  }

  // Fallback - should not happen if hasNonVehicleFilters returned true
  return { filterKey: 'unknown', filterValue: 'unknown' };
}

export default useDynamicSeo;
